/**
 * EventEmitter.ts - Type-safe event system for DEFCON TERMINAL
 *
 * Provides a simple pub/sub event system with strong typing support.
 * Used by the game engine to communicate state changes to the UI layer.
 */

/**
 * Generic event map type - extend this for specific event definitions
 */
export type EventMap = Record<string, unknown>;

/**
 * Event handler callback type
 */
export type EventHandler<T = unknown> = (data: T) => void;

/**
 * Type-safe event emitter class
 *
 * @example
 * ```ts
 * interface GameEvents {
 *   'scenario:start': { scenarioId: string };
 *   'node:change': { nodeId: string };
 *   'game:end': { score: number };
 * }
 *
 * const emitter = new EventEmitter<GameEvents>();
 * emitter.on('scenario:start', (data) => console.log(data.scenarioId));
 * emitter.emit('scenario:start', { scenarioId: 'test-001' });
 * ```
 */
export class EventEmitter<Events extends EventMap = EventMap> {
  private listeners: Map<keyof Events, Set<EventHandler<unknown>>> = new Map();
  private onceListeners: Map<keyof Events, Set<EventHandler<unknown>>> = new Map();

  /**
   * Register an event listener
   *
   * @param event - Event name to listen for
   * @param callback - Handler function to call when event is emitted
   * @returns Function to unsubscribe the listener
   */
  on<K extends keyof Events>(
    event: K,
    callback: EventHandler<Events[K]>
  ): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }

    this.listeners.get(event)!.add(callback as EventHandler<unknown>);

    // Return unsubscribe function
    return () => this.off(event, callback);
  }

  /**
   * Remove an event listener
   *
   * @param event - Event name to stop listening for
   * @param callback - Handler function to remove
   */
  off<K extends keyof Events>(
    event: K,
    callback: EventHandler<Events[K]>
  ): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.delete(callback as EventHandler<unknown>);
      if (eventListeners.size === 0) {
        this.listeners.delete(event);
      }
    }

    // Also check once listeners
    const onceEventListeners = this.onceListeners.get(event);
    if (onceEventListeners) {
      onceEventListeners.delete(callback as EventHandler<unknown>);
      if (onceEventListeners.size === 0) {
        this.onceListeners.delete(event);
      }
    }
  }

  /**
   * Register a one-time event listener that auto-removes after first call
   *
   * @param event - Event name to listen for once
   * @param callback - Handler function to call when event is emitted
   * @returns Function to unsubscribe the listener
   */
  once<K extends keyof Events>(
    event: K,
    callback: EventHandler<Events[K]>
  ): () => void {
    if (!this.onceListeners.has(event)) {
      this.onceListeners.set(event, new Set());
    }

    this.onceListeners.get(event)!.add(callback as EventHandler<unknown>);

    return () => this.off(event, callback);
  }

  /**
   * Emit an event to all registered listeners
   *
   * @param event - Event name to emit
   * @param data - Data to pass to handlers
   */
  emit<K extends keyof Events>(event: K, data: Events[K]): void {
    // Call regular listeners
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in event handler for "${String(event)}":`, error);
        }
      });
    }

    // Call once listeners and remove them
    const onceEventListeners = this.onceListeners.get(event);
    if (onceEventListeners) {
      onceEventListeners.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in once handler for "${String(event)}":`, error);
        }
      });
      this.onceListeners.delete(event);
    }
  }

  /**
   * Remove all listeners for a specific event or all events
   *
   * @param event - Optional event name. If omitted, removes all listeners.
   */
  removeAllListeners<K extends keyof Events>(event?: K): void {
    if (event !== undefined) {
      this.listeners.delete(event);
      this.onceListeners.delete(event);
    } else {
      this.listeners.clear();
      this.onceListeners.clear();
    }
  }

  /**
   * Get the number of listeners for a specific event
   *
   * @param event - Event name to count listeners for
   * @returns Number of registered listeners
   */
  listenerCount<K extends keyof Events>(event: K): number {
    const regular = this.listeners.get(event)?.size ?? 0;
    const once = this.onceListeners.get(event)?.size ?? 0;
    return regular + once;
  }

  /**
   * Get all event names that have registered listeners
   *
   * @returns Array of event names
   */
  eventNames(): (keyof Events)[] {
    const names = new Set<keyof Events>();
    this.listeners.forEach((_, key) => names.add(key));
    this.onceListeners.forEach((_, key) => names.add(key));
    return Array.from(names);
  }

  /**
   * Check if an event has any listeners
   *
   * @param event - Event name to check
   * @returns True if event has at least one listener
   */
  hasListeners<K extends keyof Events>(event: K): boolean {
    return this.listenerCount(event) > 0;
  }
}

/**
 * Create a typed event emitter with promise-based waiting
 */
export function createEventEmitter<Events extends EventMap>(): EventEmitter<Events> {
  return new EventEmitter<Events>();
}

/**
 * Wait for an event to be emitted (promise-based)
 *
 * @param emitter - Event emitter to wait on
 * @param event - Event name to wait for
 * @param timeout - Optional timeout in milliseconds
 * @returns Promise that resolves with event data
 */
export function waitForEvent<Events extends EventMap, K extends keyof Events>(
  emitter: EventEmitter<Events>,
  event: K,
  timeout?: number
): Promise<Events[K]> {
  return new Promise((resolve, reject) => {
    const unsubscribe = emitter.once(event, (data) => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
      resolve(data);
    });

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (timeout !== undefined) {
      timeoutId = setTimeout(() => {
        unsubscribe();
        reject(new Error(`Timeout waiting for event "${String(event)}"`));
      }, timeout);
    }
  });
}
